import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { MistakeType, RequestMistake } from '@workflows/types';
import {
  TuiButton,
  TuiCheckbox,
  TuiDialogContext,
  TuiInput,
  TuiLoader,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { XAlertService } from '@core/services';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  MistakesFormTranslations,
  MistakesFormTranslationsPayload,
} from '@core/types';
import {
  buildMistakesPayload,
  getMistakesErrorMessage,
  prefillMistakesForm,
} from './mistakes-form.utils';

@Component({
  selector: 'x-mistakes-form',
  imports: [
    TuiCheckbox,
    ReactiveFormsModule,
    TuiTextfield,
    TuiInput,
    TuiButton,
    TuiLoader,
    TranslatePipe,
  ],
  templateUrl: './mistakes-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MistakesFormComponent implements OnInit {
  private readonly workflowService = inject(WorkflowService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly context = inject(POLYMORPHEUS_CONTEXT, {
    optional: false,
  });

  protected mistakesTypes: MistakeType[] = [];

  /** Текущие ошибки заявки загружаются перед редактированием */
  protected readonly $loading = signal(true);
  protected readonly $loadError = signal<string | null>(null);
  protected readonly $saving = signal(false);
  /** Сколько ошибок у заявки сейчас: если есть, снятие всех отметок = очистка */
  protected readonly $existingCount = signal(0);
  /** Ошибки с типами не из списка диалога: сохраняются без изменений */
  private preservedMistakes: RequestMistake[] = [];

  private translates: Partial<MistakesFormTranslationsPayload> = {};

  protected form = new FormGroup({
    mistakes: new FormArray<
      FormGroup<{
        selected: FormControl<boolean>;
        comment: FormControl<string | null>;
      }>
    >([]),
  });

  ngOnInit() {
    const data = this.context['data'];

    this.mistakesTypes = data.mistakesTypes;
    this.buildForm(this.mistakesTypes);
    this.getTranslations();
    this.loadCurrentMistakes();
  }

  protected hasSelected(): boolean {
    return this.form.controls.mistakes.controls.some(
      group => group.controls.selected.value,
    );
  }

  /** Сохранить можно отмеченные ошибки или очистить уже существующие */
  protected canSubmit(): boolean {
    return (
      !this.$loading() &&
      !this.$loadError() &&
      !this.$saving() &&
      (this.hasSelected() || this.$existingCount() > 0)
    );
  }

  private buildForm(types: MistakeType[]): void {
    const mistakesArray = this.form.controls.mistakes;

    types.forEach(() => {
      const group = new FormGroup({
        selected: new FormControl(false, { nonNullable: true }),
        comment: new FormControl<string | null>(null),
      });

      mistakesArray.push(group);
    });
  }

  private loadCurrentMistakes(): void {
    const data = this.context['data'];

    this.$loading.set(true);
    this.$loadError.set(null);

    this.workflowService
      .getRequestMistakes(data.request.requestNumber, data.workflowId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.$loading.set(false);

          if (!response.isOk) {
            this.$loadError.set(response.failureReason);

            return;
          }

          const prefill = prefillMistakesForm(
            this.mistakesTypes,
            response.data.mistakes,
          );

          this.preservedMistakes = prefill.preserved;
          this.$existingCount.set(response.data.mistakes.length);
          prefill.selection.forEach((item, index) =>
            this.form.controls.mistakes.at(index)?.setValue(item),
          );
        },
        error: error => {
          this.$loading.set(false);
          this.$loadError.set(getMistakesErrorMessage(error) ?? '');
        },
      });
  }

  private getTranslations() {
    this.outkeepTranslateService
      .translate(Object.values(MistakesFormTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  protected onCancel(): void {
    (this.context as TuiDialogContext).completeWith();
  }

  protected onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }

    // Есть видимые отметки — обычное сохранение: ошибки типов не из списка диалога
    // сохраняются вместе с ними
    if (this.hasSelected()) {
      this.save(
        buildMistakesPayload(
          this.mistakesTypes,
          this.form.controls.mistakes.getRawValue(),
          this.preservedMistakes,
        ),
      );

      return;
    }

    // Снятие всех отметок («Удалить ошибки») удаляет все ошибки заявки,
    // включая типы не из списка диалога — только после подтверждения
    this.xAlertService
      .showDialog(
        this.translates[MistakesFormTranslations.ClearConfirmTitle] ?? '',
        this.translates[MistakesFormTranslations.ClearConfirmMessage] ?? '',
      )
      .pipe(
        filter(confirmed => Boolean(confirmed)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.save([]));
  }

  private save(mistakes: { mistakeTypeId: number; comment: string }[]): void {
    const data = this.context['data'];

    this.$saving.set(true);

    this.workflowService
      .setRequestMistakes(data.request.requestNumber, data.workflowId, mistakes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.$saving.set(false);
          this.xAlertService.success(
            (mistakes.length > 0
              ? this.translates[MistakesFormTranslations.SaveSuccess]
              : this.translates[MistakesFormTranslations.ClearSuccess]) ?? '',
          );
          (this.context as TuiDialogContext).completeWith();
        },
        error: error => {
          this.$saving.set(false);
          this.xAlertService.error(
            this.translates[MistakesFormTranslations.SaveError] ?? '',
            getMistakesErrorMessage(error),
          );
        },
      });
  }
}
